import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { KnockoutBracket } from '@/components/bracket/KnockoutBracket';
import { tableRanks } from '@/components/bracket/bracketView';
import { MainSection } from '@/components/layout/MainSection';
import { useLeagueDetailContext } from '@/layouts/LeagueDetailLayout/context';
import { useLeagueMatches } from '@/hooks/useLeagueMatches';
import { useLeagueStandings } from '@/hooks/useLeagueStandings';
import { buildKnockoutBracket } from '@/utils/knockoutBracket';
import styles from '../LeagueDetailPage.module.scss';

/**
 * `/matches/:leagueId/playoff` - the competition's knockout rounds as a
 * one-sided bracket, with placeholders for the pairs not known yet. Built from
 * the season's matches the "Matches" tab already loads and, for the order of
 * stored ties, from the stored table.
 */
const PlayoffTab: React.FC = () => {
  const { league, leagueId } = useLeagueDetailContext();
  const { t } = useTranslation(['league']);

  const { data: matchesData, isLoading: isMatchesLoading, error } = useLeagueMatches(leagueId);
  const { data: standings, isLoading: isStandingsLoading, isPlaceholderData } = useLeagueStandings(leagueId);

  const matches = matchesData?.data;
  // Placeholder data is the table of the league shown before: its places say nothing about this one.
  const groups = isPlaceholderData ? undefined : standings?.groups;

  const rounds = useMemo(
    () => (matches ? buildKnockoutBracket(matches, league.externalId, league.season, tableRanks(groups)) : []),
    [matches, groups, league.externalId, league.season],
  );

  let body: React.ReactNode;
  if (error) {
    body = <p className={styles.error}>{t('league:errorLoadingMatches')}</p>;
  } else if (isMatchesLoading || isStandingsLoading || !matches) {
    body = null;
  } else if (rounds.length === 0) {
    body = (
      <div className={styles.emptyState} data-testid="league-playoff-empty">
        <p className={styles.emptyStateText}>{t('league:playoffNotAvailable')}</p>
      </div>
    );
  } else {
    // Keyed by league: in-place league -> league navigation starts a fresh bracket at its own round.
    body = (
      <div className={styles.playoffWrap}>
        <KnockoutBracket key={leagueId} rounds={rounds} />
      </div>
    );
  }

  return (
    <MainSection
      subtitleHideOnMobile
      title={league.name}
      subtitle={t('league:playoff')}
      logoUrl={league.logoUrl || undefined}
      logoAlt={league.name}
    >
      {body}
    </MainSection>
  );
};

export default PlayoffTab;
